"use client";

import { useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

export interface Flotante {
  /** Coordenadas del panel. Van inline porque se miden en runtime. */
  style: CSSProperties;
  /** Se abrió hacia arriba porque abajo no entraba. */
  arriba: boolean;
}

const SEPARACION = 4;
const MARGEN_VIEWPORT = 8;
const ALTO_MINIMO = 120;

// En el server no hay layout: `useLayoutEffect` avisaría por consola.
const useEfectoDeLayout = typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface OpcionesFlotante {
  altoMax: number;
  /** Ancho propio del panel. Sin él, mide lo que el ancla. Se recorta al viewport y se corre a la izquierda si no entra. */
  ancho?: number;
  /** Sin `ancho`: el panel mide lo que el ancla, pero nunca menos que esto (el menú de un filtro angosto). */
  anchoMin?: number;
  /** A qué borde del ancla se pega el panel. Default `izquierda`. */
  alinear?: "izquierda" | "derecha";
}

/**
 * Dónde dibujar un panel que cuelga de un ancla (la lista de un `Select`, el
 * calendario de un `DatePicker`). Devuelve `position: fixed` en vez de un
 * portal al `body`: adentro de un `<dialog>` (top layer) un portal quedaría
 * tapado por el scrim, y como hijo del ancla hereda la capa correcta.
 *
 * Se mide al abrir y se vuelve a medir con cada scroll (en captura, para
 * agarrar el de cualquier contenedor) y cambio de tamaño. Si abajo no entra
 * `altoMax` y arriba hay más lugar, se abre hacia arriba.
 */
export function useFlotante(ancla: RefObject<HTMLElement | null>, abierto: boolean, opciones: OpcionesFlotante): Flotante {
  const { altoMax, ancho, anchoMin = 0, alinear = "izquierda" } = opciones;
  const [pos, setPos] = useState<Flotante>({ style: {}, arriba: false });

  useEfectoDeLayout(() => {
    if (!abierto) return;
    const el = ancla.current;
    if (!el) return;

    function medir() {
      const r = el!.getBoundingClientRect();
      const lugarAbajo = window.innerHeight - r.bottom - SEPARACION - MARGEN_VIEWPORT;
      const lugarArriba = r.top - SEPARACION - MARGEN_VIEWPORT;
      const arriba = lugarAbajo < Math.min(altoMax, ALTO_MINIMO * 2) && lugarArriba > lugarAbajo;
      const maxHeight = Math.max(ALTO_MINIMO, Math.min(altoMax, arriba ? lugarArriba : lugarAbajo));
      const pedido = ancho === undefined ? Math.max(r.width, anchoMin) : ancho;
      const width = Math.min(pedido, window.innerWidth - 2 * MARGEN_VIEWPORT);
      const borde = alinear === "derecha" ? r.right - width : r.left;
      const left = Math.max(MARGEN_VIEWPORT, Math.min(borde, window.innerWidth - MARGEN_VIEWPORT - width));
      setPos({
        arriba,
        style: {
          left,
          width,
          maxHeight,
          ...(arriba ? { bottom: window.innerHeight - r.top + SEPARACION } : { top: r.bottom + SEPARACION }),
        },
      });
    }

    medir();
    window.addEventListener("scroll", medir, true);
    window.addEventListener("resize", medir);
    return () => {
      window.removeEventListener("scroll", medir, true);
      window.removeEventListener("resize", medir);
    };
  }, [abierto, ancla, altoMax, ancho, anchoMin, alinear]);

  return pos;
}

/**
 * Cierra un panel cuando se hace click o tap fuera de `raiz`. El `focusout`
 * no alcanza solo: en celular, tocar un área sin foco no dispara blur.
 */
export function useClickAfuera(raiz: RefObject<HTMLElement | null>, abierto: boolean, onAfuera: () => void) {
  useEffect(() => {
    if (!abierto) return;
    function onDown(e: PointerEvent) {
      if (raiz.current && !raiz.current.contains(e.target as Node)) onAfuera();
    }
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [abierto, raiz, onAfuera]);
}
